import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { statusClass, formatDate } from "@/lib/format";
import { getIntegrationVisual, categoryColor } from "@/lib/brandIcons";
import { useTwoFactor } from "@/components/TwoFactorProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ExternalLink, Plug, Unplug, KeyRound } from "lucide-react";

const CATEGORIES = ["Tutte", "LLM · Text", "LLM · Image", "LLM · Video", "Voice", "DevOps", "Cloud", "Operations", "Payments", "Email", "Communication"];

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("Tutte");
  const [dialogFor, setDialogFor] = useState(null);
  const twoFactor = useTwoFactor();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations");
      setItems(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === "Tutte") return items;
    return items.filter((i) => i.category === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const connected = items.filter((i) => i.status === "CONNECTED").length;
    return { total: items.length, connected };
  }, [items]);

  const disconnect = async (item) => {
    try {
      await twoFactor.requireCode({
        actionLabel: `Disconnetti ${item.name}`,
        run: async (code) => {
          await api.post(`/integrations/${item.key}/disconnect`, {}, { headers: { "X-Second-Factor": code } });
          toast.success(`${item.name} disconnessa`);
          await load();
        },
      });
    } catch (_) {
      // cancelled or handled by TwoFactorProvider
    }
  };

  return (
    <div data-testid={TID.integrationsPage} className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-neutral-400 max-w-2xl">
            Piattaforme collegate a Structura. Ogni integrazione ha uno scope di sola lettura per default; le azioni di scrittura sono predisposte ma non attivate in scaffold.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatBox label="totali" value={counts.total} />
          <StatBox label="connesse" value={counts.connected} accent />
        </div>
      </div>

      {err && (
        <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
          {err}
        </div>
      )}

      {/* Filter chips */}
      <div data-testid={TID.integrationsFilter} className="flex flex-wrap gap-1.5 border-b border-[#1F1F1F] pb-3">
        {CATEGORIES.map((c) => {
          const active = filter === c;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={[
                "font-mono text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 transition-colors",
                active
                  ? "border-[#EDEDED] bg-[#EDEDED] text-black"
                  : "border-[#1F1F1F] text-neutral-400 hover:border-[#333] hover:text-[#EDEDED]",
              ].join(" ")}
            >
              {c}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((it) => (
          <IntegrationCard
            key={it.key}
            item={it}
            onConnect={() => setDialogFor(it)}
            onDisconnect={() => disconnect(it)}
          />
        ))}
      </div>

      <ConnectDialog
        item={dialogFor}
        open={Boolean(dialogFor)}
        onOpenChange={(o) => !o && setDialogFor(null)}
        onSaved={() => {
          setDialogFor(null);
          load();
        }}
      />
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#4ADE80]/50" : "border-[#1F1F1F]"} bg-[#0C0C0C] px-3 py-2`}>
      <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-neutral-500">{label}</div>
      <div className={`font-heading text-lg ${accent ? "text-[#4ADE80]" : "text-[#EDEDED]"}`}>{value}</div>
    </div>
  );
}

function IntegrationCard({ item, onConnect, onDisconnect }) {
  const { Icon, accent } = getIntegrationVisual(item.key);
  const connected = item.status === "CONNECTED";
  const catColor = categoryColor(item.category);

  return (
    <div
      data-testid={TID.integrationCard}
      className="border border-[#1F1F1F] bg-[#0C0C0C] hover:border-[#333] transition-colors flex flex-col"
    >
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-start gap-3 bg-[#141414]">
        <div
          className="w-9 h-9 border border-[#1F1F1F] flex items-center justify-center shrink-0"
          style={{ color: accent }}
        >
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#EDEDED] leading-tight truncate">{item.name}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span
              className="font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 border"
              style={{ color: catColor, borderColor: `${catColor}55` }}
            >
              {item.category}
            </span>
          </div>
        </div>
        <span
          className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 shrink-0 ${
            connected ? statusClass("UP") : "text-neutral-500 border-neutral-700"
          }`}
        >
          {connected ? "CONNESSA" : "NON CONFIG."}
        </span>
      </header>

      <div className="p-4 flex-1 flex flex-col">
        <p className="text-xs text-neutral-400 leading-relaxed">{item.description}</p>

        <div className="mt-3 pt-3 border-t border-[#1F1F1F]">
          <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-neutral-500 mb-1.5">
            Surfaces / modelli
          </div>
          <div className="flex flex-wrap gap-1">
            {item.surfaces.map((s) => (
              <span
                key={s}
                className="font-mono text-[10px] bg-[#050505] border border-[#1F1F1F] px-1.5 py-0.5 text-neutral-300"
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {connected && (
          <div className="mt-3 pt-3 border-t border-[#1F1F1F] font-mono text-[10px] text-neutral-500 space-y-1">
            <div>scope · <span className="text-neutral-300">{item.scope}</span></div>
            <div>connessa · <span className="text-neutral-300">{formatDate(item.connected_at, { withTime: true })}</span></div>
          </div>
        )}
      </div>

      <footer className="border-t border-[#1F1F1F] px-4 py-2.5 flex items-center justify-between">
        <a
          href={item.docs_url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED] inline-flex items-center gap-1"
        >
          docs
          <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
        </a>
        {connected ? (
          <Button
            data-testid={TID.integrationDisconnectBtn}
            onClick={onDisconnect}
            variant="ghost"
            className="rounded-none h-7 px-3 font-mono text-[10px] uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/10"
          >
            <Unplug className="w-3 h-3 mr-1.5" strokeWidth={1.5} />
            Disconnetti
          </Button>
        ) : (
          <Button
            data-testid={TID.integrationConnectBtn}
            onClick={onConnect}
            className="rounded-none h-7 px-3 bg-[#EDEDED] text-black hover:bg-white font-mono text-[10px] uppercase tracking-widest"
          >
            <Plug className="w-3 h-3 mr-1.5" strokeWidth={1.5} />
            Connetti
          </Button>
        )}
      </footer>
    </div>
  );
}

function ConnectDialog({ item, open, onOpenChange, onSaved }) {
  const twoFactor = useTwoFactor();
  const [apiKey, setApiKey] = useState("");
  const [scope, setScope] = useState("read");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setApiKey("");
      setScope("read");
      setErr("");
      setSubmitting(false);
    }
  }, [open]);

  const save = async () => {
    if (!item) return;
    setSubmitting(true);
    setErr("");
    try {
      await twoFactor.requireCode({
        actionLabel: `Connetti ${item.name} (scope ${scope})`,
        run: async (code) => {
          await api.post(
            `/integrations/${item.key}/connect`,
            { api_key: apiKey || null, scope },
            { headers: { "X-Second-Factor": code } },
          );
          toast.success(`${item.name} connessa · scope ${scope}`);
          onSaved?.();
        },
      });
    } catch (e) {
      if (e?.message !== "cancelled") {
        setErr(e?.response?.data?.detail || e.message || "Errore");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.integrationDialog}
        className="rounded-none border border-[#1F1F1F] bg-[#0C0C0C] text-[#EDEDED] max-w-md p-0"
      >
        <DialogHeader className="border-b border-[#1F1F1F] px-5 py-3 space-y-1">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
            structura://integrations/connect
          </div>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight">
            Connetti {item?.name || ""}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-1.5">
              <KeyRound className="w-3 h-3" strokeWidth={1.5} />
              API key <span className="text-neutral-600">(opzionale in scaffold)</span>
            </label>
            <Input
              data-testid={TID.integrationDialogKey}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={item?.credential_hint || "..."}
              className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
            <div className="font-mono text-[10px] text-neutral-500">
              La chiave viene mascherata lato server e non è verificata (scaffold).
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
              Scope
            </label>
            <div data-testid={TID.integrationDialogScope} className="grid grid-cols-3 gap-1.5">
              {["read", "write", "read+write"].map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  className={[
                    "border font-mono text-[10px] uppercase tracking-widest px-2 py-2 transition-colors",
                    scope === s
                      ? "border-[#EDEDED] bg-[#EDEDED] text-black"
                      : "border-[#1F1F1F] text-neutral-400 hover:text-[#EDEDED] hover:border-[#333]",
                  ].join(" ")}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div className="font-mono text-xs text-red-400 border border-red-900/60 bg-red-950/20 px-3 py-2">
              {err}
            </div>
          )}

          <div className="font-mono text-[10px] text-neutral-500 pt-2 border-t border-[#1F1F1F]">
            → la connessione richiede secondo fattore (scrittura di configurazione)
          </div>
        </div>

        <DialogFooter className="border-t border-[#1F1F1F] px-5 py-3 gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none font-mono text-xs uppercase tracking-widest hover:bg-[#141414]"
          >
            Annulla
          </Button>
          <Button
            data-testid={TID.integrationDialogSave}
            onClick={save}
            disabled={submitting}
            className="rounded-none bg-[#EDEDED] text-black hover:bg-white disabled:opacity-40 font-mono text-xs uppercase tracking-widest"
          >
            {submitting ? "Connessione…" : "Connetti"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
